import { useEffect, useState, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row, Col, Badge, Form, Button, OverlayTrigger, Popover, InputGroup } from "react-bootstrap";

import { ConfigContext } from "../context";
import { getPlatformsData } from "../actions/platformsActions";
import { getEventsStatusesData } from "../actions/eventsActions";
import { getCustomersStatusesData } from "../actions/customersActions";
import CustomerCardPayments from "../components/CustomerCardPayments";
import CustomerCardSidebar from "../components/CustomerCardSidebar";
import CustomerInstalls from "../components/CustomerCardInstalls";
import Loader from "../components/UI/Loader";
import Notification from "../components/UI/Notification";

export default function CustomerCard() {

	const { appConfig } = useContext(ConfigContext);
	const history = useHistory();
	const params = useParams();
	const dispatch = useDispatch();
	const platformsStore = useSelector(state => state.platforms);
	const userInfoStore = useSelector(state => state.userInfo);
	const eventsStatusesStore = useSelector(state => state.eventsStatuses);
	const customersStatusesStore = useSelector(state => state.customersStatuses)
	const [notification, setNotification] = useState({ show: false, title: "", text: "", time: "" });
	const [customerCard, setCustomerCard] = useState(null);
	const [customerCardLoaded, setCustomerCardLoaded] = useState(false);
	const [cardLoading, setCardLoading] = useState(true);
	const [lifetimeAccess, setLifetimeAccess] = useState(false);
	const [muted, setMuted] = useState(false);
	const [downloadLink, setDownloadLink] = useState(null);
	const _user_token = localStorage.getItem("userToken");
	const application_id = userInfoStore.data.selected.application;
	const customer_id = params.id;

	const statusType = {
		"-2": "danger",
		"-1": "warning",
		"0": "info",
		"1": "success"
	}

	const statusColor = {
		"-2": "text-dark-red",
		"-1": "text-dark-yellow",
		"0": "text-primary",
		"1": "text-dark-green"
	}

	const loadCustomerCard = async () => {
		const searchParams = new URLSearchParams({
			_user_token,
			application_id,
			customer_id
		});

		try {
			const response = await (await
				fetch(`/api/v1/frontend/customers/show?${searchParams}`)).json();
			if (response._code === 200) {
				setCustomerCard(response._data);
				setCustomerCardLoaded(true);
				setDownloadLink(response._data.download_link);
				setLifetimeAccess(response._data.lifetime);
				setMuted(response._data.muted);
			} else if (response._code === 500) {
				history.push(`/`);
			} else {
				throw new Error(`(code ${response._code}), ${response._msg}`);
			}
		} catch (err) {
			console.error(err);
			handleNotification({ show: true, title: "Error", text: err.message, time: "Now" });
		}
	}

	const toggleLifetime = async () => {
		try {
			const response = await (
				await fetch(`/api/v1/frontend/customers/toggle_lifetime`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ _user_token, application_id, customer_id })
				})).json();
			if (response._code === 200) {
				setLifetimeAccess(!lifetimeAccess);
			} else if (response._code === 500) {
				history.push(`/`);
			} else {
				throw new Error(`(code ${response._code}), ${response._msg}`);
			}
		} catch (err) {
			console.error(err);
			handleNotification({ show: true, title: "Error", text: err.message, time: "Now" });
		}
	}

	const toggleMuted = async () => {
		try {
			const response = await (
				await fetch(`/api/v1/frontend/customers/toggle_muted`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ _user_token, application_id, customer_id })
				})).json();
			if (response._code === 200) {
				setMuted(!muted);
			} else if (response._code === 500) {
				history.push(`/`);
			} else {
				throw new Error(`(code ${response._code}), ${response._msg}`);
			}
		} catch (err) {
			console.error(err);
			handleNotification({ show: true, title: "Error", text: err.message, time: "Now" });
		}
	}

	const createDownloadLink = async () => {
		try {
			const response = await (
				await fetch(`/api/v1/frontend/customers/create_download_link`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ _user_token, application_id, customer_id })
				})).json();
			if (response._code === 200) {
				setDownloadLink(response._data.download_link);
			} else {
				throw new Error(`(code ${response._code}), ${response._msg}`);
			}
		} catch (err) {
			console.error(err);
			handleNotification({ show: true, title: "Error", text: err.message, time: "Now" });
		}
	}

	const timestampToDate = (timestamp, withTime) => {
		if (withTime)
			return new Intl.DateTimeFormat("ru-RU", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit"
			}).format(timestamp * 1000);
		else
			return new Intl.DateTimeFormat("ru-RU", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit"
			}).format(timestamp * 1000);
	}

	const handleNotification = (data) => {
		setNotification(data);
	}

	useEffect(() => {
		loadCustomerCard();
		if (!eventsStatusesStore.loaded)
			dispatch(getEventsStatusesData(`/api/v1/frontend/event_statuses/list`, { _user_token, application_id }));
		if (!customersStatusesStore.loaded)
			dispatch(getCustomersStatusesData(`/api/v1/frontend/paying_statuses/list`, { _user_token, application_id }));
		if (!platformsStore.loaded)
			dispatch(getPlatformsData(`/api/v1/frontend/platforms/list`));
	}, [params.id])

	useEffect(() => {
		if (eventsStatusesStore.loaded && customersStatusesStore.loaded && platformsStore.loaded && customerCardLoaded) {
			setCardLoading(false);
		}
	}, [eventsStatusesStore.loaded, customersStatusesStore.loaded, platformsStore.loaded, customerCardLoaded])

	useEffect(() => {
		if (!cardLoading) history.push("/customers");
	}, [userInfoStore.data.selected.application])

	return (cardLoading ? <Loader /> :
		<Container className="py-4 px-2 px-lg-5">

			<Notification notification={notification} handleNotification={handleNotification} />

			<Row className="m-0">
				<Col xs="auto" className="px-0">
					<h4 className="SFProDisplay-Bold text-truncate m-0">
						{customerCard.customer_id}
					</h4>
				</Col>
			</Row>

			<Row className="pt-3 m-0">
				<Col xs={4} lg="auto" className="ps-0 pe-lg-4">
					<p className="opacity-50 m-0">
						{appConfig.CustomerCard.wording.text_1}
					</p>
					<p className="SFProDisplay-Semibold m-0">
						{customerCard.created_at ? timestampToDate(customerCard.created_at) : appConfig.CustomerCard.wording.text_2}
					</p>
				</Col>
				<Col xs={4} lg="auto" className="ps-0 pe-lg-4">
					<p className="opacity-50 m-0">
						{appConfig.CustomerCard.wording.text_3}
					</p>
					<p className="SFProDisplay-Semibold m-0">
						{customerCard.ltv ? customerCard.ltv : appConfig.CustomerCard.wording.text_4}
					</p>
				</Col>
				<Col xs={4} lg="auto" className="ps-0 pe-lg-4">
					<p className="opacity-50 m-0">
						{appConfig.CustomerCard.wording.text_5}
					</p>
					{customerCard.paying_status
						?
						<Badge pill bg={statusType[customerCard.paying_status]} className={`${statusColor[customerCard.paying_status]} fw-normal text-capitalize m-0`}>
							{customersStatusesStore.data.find(x => x.id === customerCard.paying_status).name}
						</Badge>
						:
						<p className="SFProDisplay-Semibold m-0">
							{appConfig.CustomerCard.wording.text_6}
						</p>
					}
				</Col>
				<Col xs={4} lg="auto" className="pt-3 pt-lg-0 ps-0 pe-lg-4">
					<p className="d-flex align-items-center m-0">
						<span className="opacity-50">{appConfig.CustomerCard.wording.text_7}</span> &nbsp;
						<OverlayTrigger
							placement="top"
							overlay={
								<Popover className="p-2">
									{appConfig.CustomerCard.wording.text_8}
								</Popover>
							}
						>
							<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M5.92816 0.140087C4.69613 0.26071 3.55744 0.714594 2.55906 1.48301C2.07287 1.8572 1.44148 2.56043 1.07661 3.13409C0.919033 3.38185 0.612286 4.00966 0.5024 4.30927C0.379537 4.64431 0.248587 5.15628 0.184148 5.55354C0.105284 6.03978 0.105284 6.96296 0.184148 7.4492C0.295656 8.13657 0.472053 8.69224 0.773983 9.30743C1.10386 9.97952 1.44687 10.4559 1.99615 11.0049C2.54546 11.5539 3.02214 11.8967 3.6946 12.2264C4.31014 12.5281 4.86614 12.7044 5.5539 12.8159C6.04042 12.8947 6.96413 12.8947 7.45064 12.8159C8.1384 12.7044 8.6944 12.5281 9.30994 12.2264C9.98241 11.8967 10.4591 11.5539 11.0084 11.0049C11.5577 10.4559 11.9007 9.97952 12.2306 9.30743C12.4497 8.86101 12.5599 8.56843 12.6779 8.11978C12.8351 7.5223 12.875 7.19478 12.875 6.50137C12.875 5.80796 12.8351 5.48044 12.6779 4.88296C12.5599 4.43431 12.4497 4.14173 12.2306 3.69531C11.9007 3.02322 11.5577 2.54682 11.0084 1.99783C10.4591 1.44886 9.98241 1.10605 9.30994 0.776352C8.695 0.474869 8.128 0.295006 7.46312 0.190471C7.09627 0.132804 6.27713 0.105915 5.92816 0.140087ZM7.19643 1.32619C8.96024 1.57205 10.4632 2.67278 11.224 4.2759C11.5651 4.99453 11.7183 5.68432 11.7183 6.50137C11.7183 6.9774 11.6777 7.32304 11.5699 7.76342C11.1173 9.61298 9.61566 11.1138 7.76505 11.5661C7.32441 11.6738 6.97858 11.7144 6.50227 11.7144C6.02597 11.7144 5.68014 11.6738 5.23949 11.5661C3.38888 11.1138 1.88722 9.61298 1.43465 7.76342C1.32688 7.32304 1.28625 6.9774 1.28625 6.50137C1.28625 6.02534 1.32688 5.67971 1.43465 5.23932C1.88777 3.38749 3.39058 1.88718 5.24476 1.43552C5.41105 1.39501 5.65251 1.34652 5.78134 1.32779C6.10837 1.2802 6.86038 1.27935 7.19643 1.32619ZM6.21279 2.88631C5.96357 2.98212 5.76037 3.25626 5.73552 3.53024C5.69499 3.97691 6.05077 4.36362 6.50227 4.36362C7.10269 4.36362 7.4733 3.70424 7.15684 3.199C6.95829 2.88195 6.56389 2.75132 6.21279 2.88631ZM6.33311 5.56527C6.17581 5.61435 6.05547 5.71804 5.98821 5.86241L5.92876 5.99004L5.92906 7.86075C5.92933 9.63022 5.93185 9.73685 5.97568 9.83123C6.14277 10.1912 6.61188 10.2825 6.89839 10.0108C6.94468 9.96692 7.00338 9.88611 7.02886 9.83123C7.07269 9.73685 7.07521 9.63022 7.07548 7.86075L7.07578 5.99004L7.01634 5.86241C6.94738 5.71443 6.82798 5.61396 6.66165 5.56392C6.52064 5.52149 6.4727 5.52169 6.33311 5.56527Z" fill="#6B7382" />
							</svg>
						</OverlayTrigger>
					</p>
					<Form.Check
						type="switch"
						checked={lifetimeAccess}
						onChange={toggleLifetime}
					/>
				</Col>
				<Col xs={4} lg="auto" className="pt-3 pt-lg-0 ps-0 pe-lg-4">
					<p className="d-flex align-items-center m-0">
						<span className="opacity-50">{appConfig.CustomerCard.wording.text_9}</span> &nbsp;
						<OverlayTrigger
							placement="top"
							overlay={
								<Popover className="p-2">
									{appConfig.CustomerCard.wording.text_10}
								</Popover>
							}
						>
							<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M5.92816 0.140087C4.69613 0.26071 3.55744 0.714594 2.55906 1.48301C2.07287 1.8572 1.44148 2.56043 1.07661 3.13409C0.919033 3.38185 0.612286 4.00966 0.5024 4.30927C0.379537 4.64431 0.248587 5.15628 0.184148 5.55354C0.105284 6.03978 0.105284 6.96296 0.184148 7.4492C0.295656 8.13657 0.472053 8.69224 0.773983 9.30743C1.10386 9.97952 1.44687 10.4559 1.99615 11.0049C2.54546 11.5539 3.02214 11.8967 3.6946 12.2264C4.31014 12.5281 4.86614 12.7044 5.5539 12.8159C6.04042 12.8947 6.96413 12.8947 7.45064 12.8159C8.1384 12.7044 8.6944 12.5281 9.30994 12.2264C9.98241 11.8967 10.4591 11.5539 11.0084 11.0049C11.5577 10.4559 11.9007 9.97952 12.2306 9.30743C12.4497 8.86101 12.5599 8.56843 12.6779 8.11978C12.8351 7.5223 12.875 7.19478 12.875 6.50137C12.875 5.80796 12.8351 5.48044 12.6779 4.88296C12.5599 4.43431 12.4497 4.14173 12.2306 3.69531C11.9007 3.02322 11.5577 2.54682 11.0084 1.99783C10.4591 1.44886 9.98241 1.10605 9.30994 0.776352C8.695 0.474869 8.128 0.295006 7.46312 0.190471C7.09627 0.132804 6.27713 0.105915 5.92816 0.140087ZM7.19643 1.32619C8.96024 1.57205 10.4632 2.67278 11.224 4.2759C11.5651 4.99453 11.7183 5.68432 11.7183 6.50137C11.7183 6.9774 11.6777 7.32304 11.5699 7.76342C11.1173 9.61298 9.61566 11.1138 7.76505 11.5661C7.32441 11.6738 6.97858 11.7144 6.50227 11.7144C6.02597 11.7144 5.68014 11.6738 5.23949 11.5661C3.38888 11.1138 1.88722 9.61298 1.43465 7.76342C1.32688 7.32304 1.28625 6.9774 1.28625 6.50137C1.28625 6.02534 1.32688 5.67971 1.43465 5.23932C1.88777 3.38749 3.39058 1.88718 5.24476 1.43552C5.41105 1.39501 5.65251 1.34652 5.78134 1.32779C6.10837 1.2802 6.86038 1.27935 7.19643 1.32619ZM6.21279 2.88631C5.96357 2.98212 5.76037 3.25626 5.73552 3.53024C5.69499 3.97691 6.05077 4.36362 6.50227 4.36362C7.10269 4.36362 7.4733 3.70424 7.15684 3.199C6.95829 2.88195 6.56389 2.75132 6.21279 2.88631ZM6.33311 5.56527C6.17581 5.61435 6.05547 5.71804 5.98821 5.86241L5.92876 5.99004L5.92906 7.86075C5.92933 9.63022 5.93185 9.73685 5.97568 9.83123C6.14277 10.1912 6.61188 10.2825 6.89839 10.0108C6.94468 9.96692 7.00338 9.88611 7.02886 9.83123C7.07269 9.73685 7.07521 9.63022 7.07548 7.86075L7.07578 5.99004L7.01634 5.86241C6.94738 5.71443 6.82798 5.61396 6.66165 5.56392C6.52064 5.52149 6.4727 5.52169 6.33311 5.56527Z" fill="#6B7382" />
							</svg>
						</OverlayTrigger>
					</p>
					<Form.Check
						type="switch"
						checked={muted}
						onChange={toggleMuted}
					/>
				</Col>
			</Row>

			<Row className="pt-3 m-0">
				<Col lg={5} className="px-0">
					<p className="SFProDisplay-Semibold d-flex align-items-center m-0">
						{appConfig.CustomerCard.wording.text_11} &nbsp;
						<OverlayTrigger
							placement="top"
							overlay={
								<Popover className="p-2">
									{appConfig.CustomerCard.wording.text_12}
								</Popover>
							}
						>
							<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M5.92816 0.140087C4.69613 0.26071 3.55744 0.714594 2.55906 1.48301C2.07287 1.8572 1.44148 2.56043 1.07661 3.13409C0.919033 3.38185 0.612286 4.00966 0.5024 4.30927C0.379537 4.64431 0.248587 5.15628 0.184148 5.55354C0.105284 6.03978 0.105284 6.96296 0.184148 7.4492C0.295656 8.13657 0.472053 8.69224 0.773983 9.30743C1.10386 9.97952 1.44687 10.4559 1.99615 11.0049C2.54546 11.5539 3.02214 11.8967 3.6946 12.2264C4.31014 12.5281 4.86614 12.7044 5.5539 12.8159C6.04042 12.8947 6.96413 12.8947 7.45064 12.8159C8.1384 12.7044 8.6944 12.5281 9.30994 12.2264C9.98241 11.8967 10.4591 11.5539 11.0084 11.0049C11.5577 10.4559 11.9007 9.97952 12.2306 9.30743C12.4497 8.86101 12.5599 8.56843 12.6779 8.11978C12.8351 7.5223 12.875 7.19478 12.875 6.50137C12.875 5.80796 12.8351 5.48044 12.6779 4.88296C12.5599 4.43431 12.4497 4.14173 12.2306 3.69531C11.9007 3.02322 11.5577 2.54682 11.0084 1.99783C10.4591 1.44886 9.98241 1.10605 9.30994 0.776352C8.695 0.474869 8.128 0.295006 7.46312 0.190471C7.09627 0.132804 6.27713 0.105915 5.92816 0.140087ZM7.19643 1.32619C8.96024 1.57205 10.4632 2.67278 11.224 4.2759C11.5651 4.99453 11.7183 5.68432 11.7183 6.50137C11.7183 6.9774 11.6777 7.32304 11.5699 7.76342C11.1173 9.61298 9.61566 11.1138 7.76505 11.5661C7.32441 11.6738 6.97858 11.7144 6.50227 11.7144C6.02597 11.7144 5.68014 11.6738 5.23949 11.5661C3.38888 11.1138 1.88722 9.61298 1.43465 7.76342C1.32688 7.32304 1.28625 6.9774 1.28625 6.50137C1.28625 6.02534 1.32688 5.67971 1.43465 5.23932C1.88777 3.38749 3.39058 1.88718 5.24476 1.43552C5.41105 1.39501 5.65251 1.34652 5.78134 1.32779C6.10837 1.2802 6.86038 1.27935 7.19643 1.32619ZM6.21279 2.88631C5.96357 2.98212 5.76037 3.25626 5.73552 3.53024C5.69499 3.97691 6.05077 4.36362 6.50227 4.36362C7.10269 4.36362 7.4733 3.70424 7.15684 3.199C6.95829 2.88195 6.56389 2.75132 6.21279 2.88631ZM6.33311 5.56527C6.17581 5.61435 6.05547 5.71804 5.98821 5.86241L5.92876 5.99004L5.92906 7.86075C5.92933 9.63022 5.93185 9.73685 5.97568 9.83123C6.14277 10.1912 6.61188 10.2825 6.89839 10.0108C6.94468 9.96692 7.00338 9.88611 7.02886 9.83123C7.07269 9.73685 7.07521 9.63022 7.07548 7.86075L7.07578 5.99004L7.01634 5.86241C6.94738 5.71443 6.82798 5.61396 6.66165 5.56392C6.52064 5.52149 6.4727 5.52169 6.33311 5.56527Z" fill="#6B7382" />
							</svg>
						</OverlayTrigger>
					</p>
					<InputGroup className="border border-grey rounded-3 mt-1 mb-3">
						<Form.Control
							defaultValue={downloadLink}
							className="border-none"
						/>
						<Button className="border-0 bg-none text-primary" onClick={createDownloadLink}>
							{downloadLink ? appConfig.CustomerCard.wording.text_13 : appConfig.CustomerCard.wording.text_14}
						</Button>
					</InputGroup>
				</Col>
			</Row>

			<Row className="m-0">

				<Col xs={12} lg={3} className="ps-0 pe-5">
					<CustomerCardSidebar
						details={customerCard.details}
						config={appConfig.CustomerCard.sidebar}
						platforms={platformsStore.data}
					/>
				</Col>

				<Col xs={12} lg={9} className="px-0">

					{customerCard.app_installs?.values?.length > 0 &&
						<Row className="py-3 m-0">
							<Col className="px-0">
								<h5 className="SFProDisplay-Bold m-0">
									{appConfig.CustomerCard.wording.text_20}
								</h5>
								<CustomerInstalls
									data={customerCard.app_installs}
									config={appConfig.CustomerCard.installs}
									platforms={platformsStore.data}
								/>
							</Col>
						</Row>
					}

					{customerCard.apple && (
						customerCard.apple.payments.values.length > 0
						|| customerCard.apple.renewal_info.values.length > 0
						|| customerCard.apple.receipts.values.length > 0
					) &&
						<Row className="py-3 m-0">
							<Col className="px-0">
								<h5 className="SFProDisplay-Bold mb-4">
									{appConfig.CustomerCard.wording.text_15}
								</h5>
								{customerCard.apple.payments.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.apple.wording.text_1}
										data={customerCard.apple.payments}
									/>
								}
								{customerCard.apple.renewal_info.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.apple.wording.text_2}
										data={customerCard.apple.renewal_info}
									/>
								}
								{customerCard.apple.receipts.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.apple.wording.text_3}
										data={customerCard.apple.receipts}
									/>}
							</Col>
						</Row>
					}

					{customerCard.google && (
						customerCard.google.payments.values.length > 0
						|| customerCard.google.renewal_info.values.length > 0
						|| customerCard.google.receipts.values.length > 0
					) &&
						<Row className="py-3 m-0">
							<Col className="px-0">
								<h5 className="SFProDisplay-Bold mb-4">
									{appConfig.CustomerCard.wording.text_16}
								</h5>
								{customerCard.google.payments.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.google.wording.text_1}
										data={customerCard.google.payments}
									/>
								}
								{customerCard.google.renewal_info.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.google.wording.text_2}
										data={customerCard.google.renewal_info}
									/>
								}
								{customerCard.google.receipts.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.google.wording.text_3}
										data={customerCard.google.receipts}
									/>
								}
							</Col>
						</Row>
					}

					{customerCard.paypal && (
						customerCard.paypal.payments.values.length > 0
						|| customerCard.paypal.renewal_info.values.length > 0
						|| customerCard.paypal.customers.values.length > 0
					) &&
						<Row className="py-3 m-0">
							<Col className="px-0">
								<h5 className="SFProDisplay-Bold mb-4">
									{appConfig.CustomerCard.wording.text_17}
								</h5>
								{customerCard.paypal.payments.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.paypal.wording.text_1}
										data={customerCard.paypal.payments}
									/>
								}
								{customerCard.paypal.renewal_info.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.paypal.wording.text_2}
										data={customerCard.paypal.renewal_info}
									/>
								}
								{customerCard.paypal.customers.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.paypal.wording.text_3}
										data={customerCard.paypal.customers}
									/>
								}
							</Col>
						</Row>
					}

					{customerCard.stripe && (
						customerCard.stripe.payments.values.length > 0
						|| customerCard.stripe.renewal_info.values.length > 0
						|| customerCard.stripe.customers.values.length > 0
					) &&
						<Row className="py-3 m-0">
							<Col className="px-0">
								<h5 className="SFProDisplay-Bold mb-4">
									{appConfig.CustomerCard.wording.text_18}
								</h5>
								{customerCard.stripe.payments.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.stripe.wording.text_1}
										data={customerCard.stripe.payments}
									/>
								}
								{customerCard.stripe.renewal_info.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.stripe.wording.text_2}
										data={customerCard.stripe.renewal_info}
									/>
								}
								{customerCard.stripe.customers.values.length > 0 &&
									<CustomerCardPayments
										title={appConfig.CustomerCard.stripe.wording.text_3}
										data={customerCard.stripe.customers}
									/>
								}
							</Col>
						</Row>
					}

					{customerCard.events && customerCard.events.length > 0 &&
						<Container fluid className="px-0">
							<Row className="py-3 m-0">
								<Col className="px-0">
									<h5 className="SFProDisplay-Bold m-0">
										{appConfig.CustomerCard.wording.text_19}
									</h5>
								</Col>
							</Row>
							{customerCard.events.map((event, i) => (
								<Row className="d-flex align-items-center justify-content-between border-top border-1 border-secondary m-0" key={i}>
									<Col xs="auto" className="py-2 px-0">
										<Badge pill bg={statusType[event.status]} className={`${statusColor[event.status]} fw-normal text-capitalize m-0`}>
											{eventsStatusesStore.data.find(x => x.id === event.status).name}
										</Badge>
										<p className="mt-1 m-0">
											{event.description}
										</p>
									</Col>
									<Col xs="auto" className="px-0">
										<p className="m-0">
											{timestampToDate(event.created_at, true)}
										</p>
									</Col>
								</Row>
							))}
						</Container>
					}

				</Col>

			</Row>

		</Container>
	);
}